import { AbstractPmsApiService } from "./abstract.service";
import { Property, User } from "../models";

/**
 * @class PropertyPmsApiService
 * @abstract
 * @extends {AbstractPmsApiService<Property>}
 */
export class PropertyPmsApiService extends AbstractPmsApiService {

    /**
     * @type {string}
     * @protected
     */
    get urlPattern() {

        return '/property(/:id)';
    }

    /**
     * @param {Object.<string, *>} attributes
     * @param {...*} params
     * @returns {Property}
     * @protected
     */
    newModel(attributes, ...params) {

        return new Property(attributes);
    }

    /**
     * @returns {Promise.<PaginationCollection<Property>>}
     */
    get(params = {}) {

        const url = this.url();
        const ignoreLB = params.hasOwnProperty('searchValue');
        return this.http
                .get(url, {
                    headers: this.authHeaders,
                    params: params,
                    ignoreLoadingBar: ignoreLB
                })
                //@todo paginator extractor & collection/pagination class with mass modeler
                .then(this.getPaginationCollector())
                .catch(this.getErrorHandler());
    }
    
    /**
     * @param {Property} property
     * @param params
     * @returns {Promise.<Property>}
     */
    show(property, params = {}) {
        
        const url = this.url({id: property.id});
        
        return this.http
                   .get(url, {
                       headers: this.authHeaders,
                       params
                   })
                   .then(this.getModeler())
                   .catch(this.getErrorHandler());
    }

    /**
     * @param {Property} property
     * @returns {Promise.<Property>}
     */
    create(property) {

        const url = this.url();

        return this.http
                .post(url, property.toObjectExcept('user', 'type', 'class', 'country', 'city'), {
                    headers: this.authHeaders
                })
                .then(this.getModeler())
                .catch(this.getErrorHandler());
    }

    /**
     * @param {Property} property
     * @returns {Promise.<Property>}
     */
    update(property) {

        const url = this.url({id: property.id});

        return this.http
                .put(url, property.toObjectExcept('user', 'type', 'class', 'country', 'city'), {
                    headers: this.authHeaders
                })
                .then(this.getModeler())
                .catch(this.getErrorHandler());
    }

    /**
     * @param {Property} property
     * @returns {Promise.<boolean>}
     */
    delete(property) {

        const url = this.url({id: property.id});

        return this.http
                .delete(url, {
                    headers: this.authHeaders
                })
                .then(this.getStatusExtractor())
                .catch(this.getErrorHandler());
    }

    /**
     * Request for unique property ID
     *
     * @returns {Promise.<Object>}
     */
    uniqueId() {

        return this.http
                   .get(this.url() + '/unique-id', {
                       headers: this.authHeaders
                   })
                   .then(this.getModeler())
                   .catch(this.getErrorHandler());
    }
    
    /**
     * Bulk status update
     * 
     * @param {Array} ids
     * @param {Integer} status
     * @returns {Promise.<boolean>}
     */
    bulkStatusUpdate(ids, status) {
        
        return this.http
                   .post(this.url() + '/bulk-status-update', {'ids': ids, 'status': status}, {
                       headers: this.authHeaders
                   })
                   .then(this.getStatusExtractor())
                   .catch(this.getErrorHandler());
    }
    
    /**
     * Bulk delete
     * 
     * @param {Array} ids
     * @returns {Promise.<boolean>}
     */
    bulkDelete(ids) {
        
        return this.http
                   .post(this.url() + '/bulk-delete', {'ids': ids}, {
                       headers: this.authHeaders
                   })
                   .then(this.getStatusExtractor())
                   .catch(this.getErrorHandler());
    }

    usersHaveAccess(property) {

        return this.http
            .get(this.url({id: property.id}) + '/users-have-access', {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor());
    }

    /**
     * @returns {Promise.<PaginationCollection<Property>>}
     */
    getDeleted(params = {}) {

        const url = this.url({
            id: 'deleted',
        });
        const ignoreLB = params.hasOwnProperty('searchValue');

        return this.http
            .post(url, params, {
                headers: this.authHeaders,
                ignoreLoadingBar: ignoreLB
            })
            .then(this.getPaginationCollector())
            .catch(this.getErrorHandler());
    }

    restore(property) {

        const url = this.url({
            id: property.id,
        }) + '/restore';

        return this.http
            .post(url, property.toObject('id', 'name', 'name_ar'), {
                headers: this.authHeaders,
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }
}



// WEBPACK FOOTER //
// ./src/pms-api/services/property.service.js